import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LOCALES, LOCALE_LABELS, useLocale, useT } from '../lib/i18n';

interface Props {
  /** Shrinks the control for the control bar, where room is short. */
  compact?: boolean;
  /** Extra classes for the wrapper, so a call site can widen the control. */
  className?: string;
}

/**
 * The language switcher.
 *
 * It lives on every screen that has words on it rather than only on the home
 * screen. A club that starts a match in the wrong language should not have to
 * abandon the match to fix it — and the person who needs the switch most is
 * usually the one who cannot read the screen they are being told to go back
 * to. The choice is stored globally, so changing it here also changes the home
 * screen, the phone remote and every match after this one.
 *
 * It is built rather than borrowed. A native `<select>` was here first and it
 * was the right first answer — one element, keyboard and screen-reader support
 * for free, and the platform's own picker on a phone. What it cannot do is
 * behave the same way twice: the popup is drawn by the operating system, so
 * its arrow, its list and above all *what a second tap does* are outside the
 * page's control. On a touch scoreboard the second tap is the whole
 * interaction — somebody opens the list, changes their mind, and taps the
 * control again to put it away — and a native select on some platforms
 * dismisses and immediately reopens, which reads as a control that is stuck.
 *
 * So: a button that toggles, a list that closes on a choice, on a tap
 * outside, or on Escape, and arrow keys to move through it.
 */
export function LanguageSelect({ compact = false, className = '' }: Props) {
  const { locale, setLocale } = useLocale();
  const t = useT();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLUListElement>(null);
  const [at, setAt] = useState({ right: 0, top: 0 });

  // The list is rendered into the body, so it has to be told where the button
  // is. The board is a fixed-size box with `overflow: hidden` on the way down
  // to this control — a menu left in the flow was laid out correctly and then
  // clipped to nothing by the first ancestor that hides its overflow.
  useLayoutEffect(() => {
    if (!open || !trigger.current) return;
    const r = trigger.current.getBoundingClientRect();
    setAt({ right: window.innerWidth - r.right, top: r.bottom + 6 });
  }, [open]);

  // A list that can only be closed by choosing from it is a trap: opened by
  // mistake, it has to be as easy to abandon as it was to open.
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent): void => {
      const target = e.target as Node;
      if (!box.current?.contains(target) && !menu.current?.contains(target)) setOpen(false);
    };
    const escape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const step = (by: number): void => {
    const i = LOCALES.indexOf(locale);
    const next = LOCALES[(i + by + LOCALES.length) % LOCALES.length];
    if (next) setLocale(next);
  };

  return (
    <div ref={box} className={`relative inline-flex shrink-0 items-center ${className}`}>
      <button
        ref={trigger}
        type="button"
        aria-label={t('home.language')}
        aria-haspopup="listbox"
        aria-expanded={open}
        // `onPointerDown` rather than `onClick`, so the toggle is decided
        // before the outside-click handler above can see the same press and
        // close what this is about to open.
        onPointerDown={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            step(1);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            step(-1);
          }
        }}
        className={[
          'flex w-full cursor-pointer items-center justify-between gap-1 rounded-xl bg-black/40',
          'font-semibold text-white outline-none ring-1 ring-white/10 hover:bg-black/60',
          'focus-visible:ring-2 focus-visible:ring-white/40',
          compact ? 'py-1.5 pl-3 pr-2.5 text-sm' : 'py-2 pl-3 pr-2.5 text-sm',
        ].join(' ')}
      >
        <span className="truncate">{LOCALE_LABELS[locale]}</span>

        {/* The chevron is drawn rather than left to the browser: a native
            select paints its own arrow, but not the same one everywhere, and
            this one can also turn over to say the list is open. */}
        <svg
          aria-hidden
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`h-3.5 w-3.5 shrink-0 text-white/60 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        >
          <path d="M2.5 4.5 6 8l3.5-3.5" />
        </svg>
      </button>

      {open &&
        createPortal(
          /* Opens downward from the control, and over everything under it. The
             control bar is the top of the board, so there is always room below
             and never any above. */
          <ul
            ref={menu}
            role="listbox"
            aria-label={t('home.language')}
            className="fixed z-50 overflow-hidden rounded-xl bg-hud-deep py-1 shadow-2xl
                       ring-1 ring-white/15"
            style={{ right: at.right, top: at.top, minWidth: trigger.current?.offsetWidth }}
          >
            {LOCALES.map((code) => (
              <li key={code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={code === locale}
                  onClick={() => {
                    setLocale(code);
                    setOpen(false);
                  }}
                  className={`block w-full whitespace-nowrap px-3 py-2 text-left text-sm
                              font-semibold transition hover:bg-white/10 ${
                                code === locale ? 'text-white' : 'text-white/70'
                              }`}
                >
                  {LOCALE_LABELS[code]}
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
